import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import { FileVideo, FileText, FileSearch, Play, Loader2, AlertCircle, CheckCircle2, Clock, XCircle, Upload } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { useOrganization } from '@/hooks/useOrganization';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import contentLibraryService, { Video, Presentation } from '@/lib/api/contentLibrary';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { VideoUploadDialog } from '@/components/content-library/VideoUploadDialog';
import { PresentationUploadDialog } from '@/components/content-library/PresentationUploadDialog';
import { toast } from 'sonner';

const ContentLibraryPage: React.FC = () => {
  const { currentOrganization } = useOrganization();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const orgId = currentOrganization?.id;

  // Upload dialog states
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [isPresentationUploadDialogOpen, setIsPresentationUploadDialogOpen] = useState(false);

  // Upload video mutation (3-step signed URL flow)
  const uploadMutation = useMutation({
    mutationFn: async (data: {
      file: File;
      title?: string;
      customInstructions?: string;
      metadataInstructions?: string;
      playlistPolicy: 'mixable' | 'standalone';
      playlistGroup?: string;
    }) => {
      if (!orgId) throw new Error('Organization ID required');

      // Step 1: Request signed upload URL
      const requestResponse = await contentLibraryService.requestUpload({
        organization_id: orgId,
        filename: data.file.name,
        title: data.title,
        playlist_policy: data.playlistPolicy,
        playlist_group: data.playlistGroup,
      });
      if (!requestResponse.success || !requestResponse.data) {
        throw new Error(requestResponse.error || 'Failed to get upload URL');
      }
      const { video_id, upload_url } = requestResponse.data;

      // Step 2: Upload file directly to signed URL
      await contentLibraryService.uploadToSignedUrl(upload_url, data.file);

      // Step 3: Confirm upload and start processing (always auto with metadata)
      const confirmResponse = await contentLibraryService.confirmUpload(video_id, {
        segmentation_mode: 'auto',
        custom_instructions: data.customInstructions,
        metadata_instructions: data.metadataInstructions,
        playlist_policy: data.playlistPolicy,
        playlist_group: data.playlistGroup ?? null,
      });
      if (!confirmResponse.success || !confirmResponse.data) {
        throw new Error(confirmResponse.error || 'Failed to confirm upload');
      }

      return {
        video_id,
        status: confirmResponse.data.status,
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-videos', orgId] });
      toast.success('Video uploaded successfully. AI segmentation in progress.');
      setIsUploadDialogOpen(false);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Upload presentation mutation
  const uploadPresentationMutation = useMutation({
    mutationFn: async (data: {
      file: File;
      title?: string;
      description?: string;
    }) => {
      if (!orgId) throw new Error('Organization ID required');
      const response = await contentLibraryService.uploadPresentation({
        organization_id: orgId,
        file: data.file,
        title: data.title,
        description: data.description,
      });
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to upload presentation');
      }
      return response.data;
    },
    retry: false,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['organization-presentation', orgId] });
      toast.success('Presentation uploaded. Processing slides…');
      setIsPresentationUploadDialogOpen(false);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Fetch videos
  const { data: videosData, isLoading: videosLoading, error: videosError } = useQuery({
    queryKey: ['organization-videos', orgId],
    queryFn: async () => {
      if (!orgId) throw new Error('Organization ID required');
      try {
        const response = await contentLibraryService.getOrganizationVideos(orgId);
        console.log('ContentLibraryPage - Videos API response:', response);
        if (!response.success || !response.data) {
          throw new Error(response.error || 'Failed to fetch videos');
        }
        console.log('ContentLibraryPage - Videos data:', response.data);
        console.log('ContentLibraryPage - First video:', response.data.videos?.[0]);
        return response.data;
      } catch (error) {
        console.error('Error fetching videos:', error);
        throw error;
      }
    },
    enabled: !!orgId,
    retry: false,
  });

  // Fetch presentations
  const { data: presentationData, isLoading: presentationLoading, error: presentationError } = useQuery({
    queryKey: ['organization-presentation', orgId],
    queryFn: async () => {
      if (!orgId) throw new Error('Organization ID required');
      try {
        const response = await contentLibraryService.getOrganizationPresentationDeck(orgId);
        console.log('ContentLibraryPage - Presentation API response:', response);
        if (!response.success || !response.data) {
          if (response.error?.toLowerCase().includes('no active presentation deck')) {
            return { deck: null, slides: [] };
          }
          throw new Error(response.error || 'Failed to fetch presentation');
        }
        console.log('ContentLibraryPage - Presentation data:', response.data);
        console.log('ContentLibraryPage - Deck object:', response.data.deck);
        return response.data;
      } catch (error) {
        console.error('Error fetching presentation:', error);
        throw error;
      }
    },
    enabled: !!orgId,
    retry: false,
  });

  const videos = videosData?.videos || [];
  const presentation = presentationData?.deck ? {
    ...presentationData.deck,
    slides: presentationData.slides || []
  } : null;
  const isPresentationReady = presentation
    ? presentation.slides.length > 0 && presentation.slides.length >= presentation.total_slides
    : false;

  // Show loading if no organization yet
  if (!currentOrganization) {
    return (
      <div className="flex min-h-0 flex-col">
        <div className="flex items-center justify-center h-full">
          <div className="text-center text-subtle">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
            <p>Loading organization...</p>
          </div>
        </div>
      </div>
    );
  }

  const getStatusLabel = (status: Video['status']) => {
    switch (status) {
      case 'ready':
        return 'Ready';
      case 'failed':
      case 'error':
        return 'Failed';
      case 'pending':
        return 'Pending';
      case 'pending_segmentation':
      case 'processing':
        return 'Processing';
      case 'segmenting':
        return 'Segmenting';
      case 'adding_to_collection':
        return 'Finalizing';
      default:
        return status;
    }
  };

  const getStatusPill = (status: Video['status']) => {
    if (status === 'ready') {
      return {
        label: 'Ready',
        className: 'bg-black/5 text-success dark:bg-white/10',
        Icon: CheckCircle2,
        iconClassName: 'text-success'
      };
    }
    if (status === 'failed' || status === 'error') {
      return {
        label: 'Failed',
        className: 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-300',
        Icon: XCircle,
        iconClassName: 'text-red-600 dark:text-red-300'
      };
    }
    if (status === 'pending') {
      return {
        label: 'Pending',
        className: 'bg-black/5 text-muted-foreground dark:bg-white/10',
        Icon: Clock,
        iconClassName: 'text-muted-foreground'
      };
    }
    return {
      label: getStatusLabel(status),
      className: 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300',
      Icon: Loader2,
      iconClassName: 'text-blue-600 dark:text-blue-300 animate-spin'
    };
  };

  return (
    <>
      <Helmet>
        <title>Asset Library | Hobbes</title>
      </Helmet>
      <div className="flex min-h-0 flex-col">
        <PageHeader title="Asset Library" />
        <div className="flex-1 px-4 sm:px-6 md:px-8 pb-8 pt-8 space-y-10">
          {/* Presentation Section */}
          <section className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-icon" />
                <h2 className="text-base font-semibold text-foreground">Presentation Deck</h2>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsPresentationUploadDialogOpen(true)}
                className="h-8 gap-2 rounded-lg border-[hsl(var(--input))] bg-card text-muted-foreground shadow-[inset_0_-2px_0_rgba(10,13,18,0.05)] hover:bg-secondary dark:bg-secondary dark:hover:bg-accent"
              >
                <Upload className="h-3.5 w-3.5" />
                Upload Presentation
              </Button>
            </div>

            {presentationLoading ? (
              <div className="h-[271px] rounded-[14px] bg-secondary border border-[hsl(var(--input))] animate-pulse" />
            ) : presentationError ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Failed to load presentation: {presentationError instanceof Error ? presentationError.message : 'Unknown error'}
                </AlertDescription>
              </Alert>
            ) : presentation ? (
              <div className="flex flex-wrap gap-4">
                <button
                  type="button"
                  className={`w-[288px] rounded-[8px] border border-[hsl(var(--input))] bg-secondary overflow-hidden text-left ${
                    isPresentationReady ? 'cursor-pointer' : 'cursor-not-allowed opacity-70'
                  }`}
                  onClick={() => {
                    if (isPresentationReady) {
                      navigate(`/content-library/presentation/${presentation.deck_id}`);
                    }
                  }}
                >
                  <div className="h-[162px] flex items-center justify-center border-b border-[hsl(var(--input))]">
                    <FileText className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div className="p-4 space-y-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium text-foreground truncate">{presentation.title || presentation.filename}</p>
                      {isPresentationReady ? (
                        <span className="inline-flex items-center gap-1 rounded-[4px] bg-black/5 px-2 py-1 text-[12px] text-success dark:bg-white/10">
                          <CheckCircle2 className="h-3 w-3 text-success" />
                          Ready
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-[4px] bg-blue-50 px-2 py-1 text-[12px] text-blue-600 dark:bg-blue-900/30 dark:text-blue-300">
                          <Loader2 className="h-3 w-3 animate-spin text-blue-600 dark:text-blue-300" />
                          Processing
                        </span>
                      )}
                    </div>
                    <p className="text-[12px] text-muted-foreground">
                      Uploaded {new Date(presentation.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </button>
              </div>
            ) : (
              <div className="rounded-[14px] bg-secondary">
                <div className="h-[271px] rounded-[12px] border border-[hsl(var(--input))] flex items-center justify-center">
                  <div className="flex flex-col items-center gap-2 text-sm text-muted-foreground">
                    <FileSearch className="h-8 w-8 text-subtle" />
                    <div className="text-center space-y-1">
                      <p className="font-medium">No presentations yet.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* Videos Section */}
          <section className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <FileVideo className="h-4 w-4 text-icon" />
                <h2 className="text-base font-semibold text-foreground">Demos ({videos.length})</h2>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsUploadDialogOpen(true)}
                className="h-8 gap-2 rounded-lg border-[hsl(var(--input))] bg-card text-muted-foreground shadow-[inset_0_-2px_0_rgba(10,13,18,0.05)] hover:bg-secondary dark:bg-secondary dark:hover:bg-accent"
              >
                <Upload className="h-3.5 w-3.5" />
                Upload Demo
              </Button>
            </div>

            {videosLoading ? (
              <div className="flex flex-wrap gap-4">
                {[1, 2].map((i) => (
                  <div key={i} className="h-[214px] w-[288px] rounded-[8px] border border-[hsl(var(--input))] bg-secondary animate-pulse" />
                ))}
              </div>
            ) : videosError ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Failed to load videos: {videosError instanceof Error ? videosError.message : 'Unknown error'}
                </AlertDescription>
              </Alert>
            ) : videos.length > 0 ? (
              <div className="flex flex-wrap gap-4">
                {videos.map((video) => {
                  const isClickable = video.status === 'ready';
                  const status = getStatusPill(video.status);
                  const StatusIcon = status.Icon;

                  return (
                    <button
                      key={video.video_id}
                      type="button"
                      className={`w-[288px] rounded-[8px] border border-[hsl(var(--input))] bg-secondary overflow-hidden text-left ${
                        isClickable ? 'cursor-pointer' : 'cursor-not-allowed opacity-70'
                      }`}
                      onClick={() => isClickable && navigate(`/content-library/video/${video.video_id}`)}
                    >
                      <div className="h-[162px] flex items-center justify-center border-b border-[hsl(var(--input))]">
                        <Play className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <div className="p-4 space-y-1">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium text-foreground truncate">
                            {video.title || video.filename}
                          </p>
                          <span className={`inline-flex items-center gap-1 rounded-[4px] px-2 py-1 text-[12px] ${status.className}`}>
                            <StatusIcon className={`h-3 w-3 ${status.iconClassName}`} />
                            {status.label}
                          </span>
                        </div>
                        <p className="text-[12px] text-muted-foreground">
                          Uploaded {new Date(video.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-[14px] bg-secondary">
                <div className="h-[271px] rounded-[12px] border border-[hsl(var(--input))] flex items-center justify-center">
                  <div className="flex flex-col items-center gap-2 text-sm text-muted-foreground">
                    <FileSearch className="h-8 w-8 text-subtle" />
                    <div className="text-center space-y-1">
                      <p className="font-medium">No demos yet.</p>
                      <p>Use <Link to="/demo-studio" className="text-foreground underline underline-offset-2 hover:text-foreground/80">Hobbes Studio</Link> to record demos for your agent to reference.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>

        {/* Video Upload Dialog */}
        <VideoUploadDialog
          open={isUploadDialogOpen}
          onOpenChange={setIsUploadDialogOpen}
          onUpload={async (data) => {
            await uploadMutation.mutateAsync(data);
          }}
          isUploading={uploadMutation.isPending}
        />

        {/* Presentation Upload Dialog */}
        <PresentationUploadDialog
          open={isPresentationUploadDialogOpen}
          onOpenChange={setIsPresentationUploadDialogOpen}
          onUpload={async (data) => {
            await uploadPresentationMutation.mutateAsync(data);
          }}
          isUploading={uploadPresentationMutation.isPending}
        />
    </>
  );
};

export default ContentLibraryPage;
